<template>
  <div
    class="min-h-screen bg-slate-50 dark:bg-slate-900"
    :dir="lang === 'ar' ? 'rtl' : 'ltr'"
  >
    <Navbar />

    <section class="container py-20 md:py-28">
      <div class="w-full max-w-4xl mx-auto space-y-4">
        <div
          class="p-5 bg-white border rounded-xl border-slate-200 dark:bg-slate-800 dark:border-slate-700"
        >
          <div class="flex flex-wrap items-center justify-between gap-3 pb-4 mb-4 border-b border-slate-200/70 dark:border-slate-700/70">
            <div>
              <h4
                class="text-base font-semibold text-slate-900 dark:text-white"
              >
                {{ t("user_type") }}
              </h4>
              <p class="text-xs text-slate-500 dark:text-slate-400">
                {{ t("selected") }}:
                <span class="font-semibold text-primary">
                  {{ currentClientTypeLabel }}
                </span>
              </p>
            </div>

            <div class="flex flex-wrap gap-2">
              <RouterLink
                v-for="tItem in clientTypes"
                :key="tItem.key"
                :to="toPath(tItem.key)"
                class="px-4 py-2 text-xs font-semibold transition border rounded-lg"
                :class="[
                  lang === 'ar' ? 'text-right' : 'text-left',
                  isActiveType(tItem.key)
                    ? 'border-primary/60 text-primary bg-secondary/20'
                    : 'border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800',
                ]"
              >
                {{ tItem.label }}
              </RouterLink>
            </div>
          </div>

          <div class="mb-3">
            <h3 class="text-sm font-semibold text-slate-900 dark:text-white">
              {{ t("consultant_info") || "Consultant Information" }}
            </h3>
            <p class="text-xs text-slate-500 dark:text-slate-400">
              {{
                t("consultant_info_hint") ||
                "Professional details used to set up your consultant profile."
              }}
            </p>
          </div>
          <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
            <BaseInput
              :label="t('consultant_name_label')"
              v-model="form.consultant.name"
              :error="fieldError('consultant.name')"
              :placeholder="t('consultant_name_ph')"
            />

            <BaseInput
              :label="t('consultant_license_label')"
              v-model="form.consultant.license_number"
              :error="fieldError('consultant.license_number')"
              inputmode="numeric"
              :placeholder="t('consultant_license_ph')"
            />

            <BaseInput
              :label="t('specialization')"
              v-model="form.consultant.specialization"
              :error="fieldError('consultant.specialization')"
              :placeholder="t('specialization_ph')"
            />

            <BaseInput
              :label="t('degree')"
              v-model="form.consultant.qualification"
              :error="fieldError('consultant.qualification')"
              :placeholder="t('degree_ph')"
            />
          </div>

          <div class="my-5 h-px bg-slate-200 dark:bg-slate-700"></div>

          <div class="mb-3">
            <h3 class="text-sm font-semibold text-slate-900 dark:text-white">
              {{ t("account_info") || "Account Information" }}
            </h3>
            <p class="text-xs text-slate-500 dark:text-slate-400">
              {{
                t("account_info_hint") ||
                "Login details and contact information for this account."
              }}
            </p>
          </div>

          <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
            <BaseInput
              :label="t('official_email_label')"
              v-model="form.user.email"
              :error="fieldError('user.email')"
              type="email"
              :placeholder="t('official_email_ph')"
            />

            <BaseInput
              :label="t('mobile')"
              v-model="form.user.phone"
              :error="fieldError('user.phone')"
              inputmode="tel"
              :placeholder="t('mobile_ph')"
            />

            <BaseInput
              :label="t('password')"
              v-model="form.user.password"
              :error="fieldError('user.password')"
              type="password"
              :placeholder="t('password_ph')"
            />

            <BaseInput
              :label="t('password_confirm')"
              v-model="form.user.password_confirmation"
              :error="fieldError('user.password_confirmation')"
              type="password"
              :placeholder="t('password_confirm_ph')"
            />

            <!-- Declarations -->
            <div class="md:col-span-2">
              <div
                class="p-4 border rounded-xl border-slate-200 dark:border-slate-700 bg-slate-50/70 dark:bg-slate-800/60"
              >
                <div class="flex items-center gap-2 mb-3">
                  <span
                    class="inline-flex items-center justify-center rounded-full size-7 bg-secondary/30 text-primary ring-1 ring-black/5"
                  >
                    <i class="mdi mdi-shield-check-outline text-[16px]"></i>
                  </span>

                  <h5
                    class="text-sm font-semibold text-slate-900 dark:text-white"
                  >
                    {{ t("declarations") }}
                  </h5>
                </div>

                <div class="space-y-4">
                  <!-- Accept Data -->
                  <div
                    class="p-3 border rounded-xl bg-slate-50/70 dark:bg-slate-800/60 border-slate-200 dark:border-slate-700"
                    :class="
                      fieldError('acceptData')
                        ? 'border-red-300 dark:border-red-500/40'
                        : ''
                    "
                  >
                    <label
                      class="flex items-start gap-3 text-sm leading-relaxed cursor-pointer text-slate-700 dark:text-slate-300"
                    >
                      <input
                        type="checkbox"
                        v-model="form.acceptData"
                        class="mt-1 rounded border-slate-300 text-primary focus:ring-primary"
                      />
                      <span>{{ t("accept_data_text") }}</span>
                    </label>

                    <transition name="fade">
                      <div
                        v-if="fieldError('acceptData')"
                        class="mt-2 flex items-start gap-2 text-xs text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-lg px-2.5 py-2"
                      >
                        <i
                          class="mdi mdi-alert-circle-outline text-[14px] mt-0.5"
                        ></i>
                        <span>{{ fieldError("acceptData") }}</span>
                      </div>
                    </transition>
                  </div>

                  <!-- Accept Terms -->
                  <div
                    class="p-3 border rounded-xl bg-slate-50/70 dark:bg-slate-800/60 border-slate-200 dark:border-slate-700"
                    :class="
                      fieldError('acceptTerms')
                        ? 'border-red-300 dark:border-red-500/40'
                        : ''
                    "
                  >
                    <label
                      class="flex items-start gap-3 text-sm leading-relaxed cursor-pointer text-slate-700 dark:text-slate-300"
                    >
                      <input
                        type="checkbox"
                        v-model="form.acceptTerms"
                        class="mt-1 rounded border-slate-300 text-primary focus:ring-primary"
                      />
                      <span>
                        {{ t("accept_terms_prefix") }}
                        <RouterLink
                          to="/terms-and-conditions"
                          class="text-primary underline underline-offset-2 hover:opacity-80"
                        >
                          {{ t("terms_and_policy") }}
                        </RouterLink>
                        {{ t("accept_terms_suffix") }}
                      </span>
                    </label>

                    <transition name="fade">
                      <div
                        v-if="fieldError('acceptTerms')"
                        class="mt-2 flex items-start gap-2 text-xs text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-lg px-2.5 py-2"
                      >
                        <i
                          class="mdi mdi-alert-circle-outline text-[14px] mt-0.5"
                        ></i>
                        <span>{{ fieldError("acceptTerms") }}</span>
                      </div>
                    </transition>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="flex items-center justify-end mt-6">
            <button
              class="h-10 px-6 text-sm font-semibold rounded-md bg-secondary hover:bg-secondary/90 disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
              :disabled="isSubmitting"
              @click="submit"
            >
              <span
                v-if="isSubmitting"
                class="inline-block size-4 rounded-full border-2 border-black/30 border-t-black animate-spin"
                aria-hidden="true"
              ></span>
              <span>{{
                isSubmitting ? t("submitting") || "Submitting..." : t("save")
              }}</span>
            </button>
          </div>

          <!-- API error -->
          <p v-if="errors.api" class="mt-3 text-sm text-red-600">
            {{ errors.api }}
          </p>
          <p
            v-if="errors.apiDetails"
            class="mt-1 text-xs text-red-500 break-words"
          >
            {{ errors.apiDetails }}
          </p>
        </div>
      </div>

      <Toast
        :show="toast.show"
        :type="toast.type"
        :message="toast.message"
        @update:show="toast.show = $event"
      />
      <!-- </div> -->
    </section>

    <Footer />
  </div>
</template>

<script>
import { messages } from "@/i18n";
import api from "@/services/api";
import { clearAuthCookies } from "@/utils/authSession";
import { setVerifySessionFromApi } from "@/utils/pendingVerification";
import Navbar from "@/components/Navbar.vue";
import BaseInput from "@/components/BaseInput.vue";
import BaseSelect from "@/components/BaseSelect.vue";
import BaseTextarea from "@/components/BaseTextarea.vue";
import Footer from "@/components/Footer.vue";
import Toast from "@/components/Toast.vue";

export default {
  components: {
    Navbar,
    BaseInput,
    BaseSelect,
    BaseTextarea,
    Toast,
    Footer,
  },

  data() {
    return {
      messages,
      toast: { show: false, type: "success", message: "" },

      lang: (
        document.documentElement.lang ||
        localStorage.getItem("app_lang") ||
        "ar"
      )
        .toLowerCase()
        .startsWith("en")
        ? "en"
        : "ar",

      _langObserver: null,

      // ✅ submission state
      isSubmitting: false,

      // ✅ payload-aligned shape
      form: {
        consultant: {
          name: "",
          license_number: "",
          specialization: "",
          qualification: "",
        },
        user: {
          email: "",
          phone: "",
          password: "",
          password_confirmation: "",
        },
        acceptData: false,
        acceptTerms: false,
      },

      selectedClientType: "consultant",
      clientTypes: [
        { key: "client", label: "" },
        { key: "consultant", label: "" },
        { key: "office", label: "" },
      ],

      errors: {},
      registeredUserId: null,
    };
  },

  mounted() {
    this.lang = (
      document.documentElement.lang ||
      localStorage.getItem("app_lang") ||
      "ar"
    )
      .toLowerCase()
      .startsWith("en")
      ? "en"
      : "ar";

    window.addEventListener("app:lang-changed", this.onLangChanged);

    this._langObserver = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.type === "attributes" && m.attributeName === "lang") {
          const htmlLang = document.documentElement.lang || "ar";
          this.lang = htmlLang.toLowerCase().startsWith("en") ? "en" : "ar";
        }
      }
    });
    this._langObserver.observe(document.documentElement, { attributes: true });

    this.refreshI18nLabels();
  },

  watch: {
    lang() {
      this.refreshI18nLabels();
    },
  },

  computed: {
    currentClientTypeLabel() {
      const m = this.clientTypes.find((t) => t.key === this.selectedClientType);
      return m ? m.label : "";
    },
  },

  methods: {
    t(key) {
      return (this.messages[this.lang] && this.messages[this.lang][key]) || key;
    },

    onLangChanged(e) {
      this.lang = e.detail;
    },

    isSaudiPhone(v) {
      const s = String(v || "").trim();
      // Saudi mobile: 05XXXXXXXX
      return /^05\d{8}$/.test(s);
    },

    refreshI18nLabels() {
      this.clientTypes = [
        { key: "client", label: this.t("type_client") },
        { key: "consultant", label: this.t("type_consultant") },
        { key: "office", label: this.t("type_office") },
      ];
    },

    validateAll() {
      this.errors = {};

      if (!this.form.consultant.name?.trim())
        this.errors["consultant.name"] = this.t("consultant_name_required");

      if (!this.form.consultant.license_number?.trim())
        this.errors["consultant.license_number"] = this.t("license_required");

      if (!this.form.consultant.specialization?.trim())
        this.errors["consultant.specialization"] = this.t(
          "specialization_required",
        );

      if (!this.form.consultant.qualification?.trim())
        this.errors["consultant.qualification"] = this.t("degree_required");

      if (!this.form.user.email?.trim())
        this.errors["user.email"] = this.t("email_required");

      if (!this.form.user.phone?.trim())
        this.errors["user.phone"] = this.t("phone_required");
      else if (!this.isSaudiPhone(this.form.user.phone))
        this.errors["user.phone"] = this.t("invalid_phone");

      if (!this.form.user.password)
        this.errors["user.password"] = this.t("password_required");

      if (!this.form.user.password_confirmation)
        this.errors["user.password_confirmation"] = this.t(
          "password_confirm_required",
        );

      if (
        this.form.user.password &&
        this.form.user.password_confirmation &&
        this.form.user.password !== this.form.user.password_confirmation
      ) {
        this.errors["user.password_confirmation"] =
          this.t("password_not_match");
      }

      if (!this.form.acceptData)
        this.errors.acceptData = this.t("accept_data_required");

      if (!this.form.acceptTerms)
        this.errors.acceptTerms = this.t("accept_terms_required");

      return Object.keys(this.errors).length === 0;
    },

    fieldError(key) {
      const v = this.errors?.[key];
      if (!v) return null;
      return Array.isArray(v) ? v.join(" • ") : v;
    },

    toPath(key) {
      if (key === "client") return "/registerClient";
      if (key === "consultant") return "/regiserConsultant";
      if (key === "office") return "/registerOffice";
      return "#";
    },

    isActiveType(key) {
      const path = this.$route.path;
      if (key === "client") return path.startsWith("/registerClient");
      if (key === "consultant") return path.startsWith("/regiserConsultant");
      if (key === "office") return path.startsWith("/registerOffice");
      return false;
    },

    mapApiErrors(apiErrors) {
      const mapped = {};
      const requiredKeyByField = {
        "consultant.name": "consultant_name_required",
        "consultant.license_number": "license_required",
        "consultant.specialization": "specialization_required",
        "consultant.qualification": "degree_required",
        "user.email": "email_required",
        "user.phone": "phone_required",
        "user.password": "password_required",
        "user.password_confirmation": "password_confirm_required",
        acceptData: "accept_data_required",
        acceptTerms: "accept_terms_required",
      };

      const resolveMessage = (key, msg) => {
        const normalized = String(msg || "")
          .trim()
          .toLowerCase();
        if (normalized === "invalid_phone") return this.t("invalid_phone");
        if (normalized === "invalid_email") return this.t("invalid_email");
        if (normalized === "password_min_8") return this.t("password_min_8");
        if (
          normalized === "required" ||
          normalized === "validation_required" ||
          normalized.includes("required")
        ) {
          const reqKey = requiredKeyByField[key];
          return reqKey ? this.t(reqKey) : this.t("required");
        }
        if (
          key === "user.password_confirmation" &&
          (normalized.includes("match") || normalized.includes("confirmation"))
        )
          return this.t("password_not_match");
        return msg;
      };

      Object.keys(apiErrors || {}).forEach((key) => {
        const msg = Array.isArray(apiErrors[key])
          ? apiErrors[key][0]
          : apiErrors[key];
        mapped[key] = resolveMessage(key, msg);
      });
      return mapped;
    },

    async submit() {
      if (this.isSubmitting) return;

      // ✅ button loading start
      this.isSubmitting = true;

      this.errors = {};
      const ok = this.validateAll();
      if (!ok) {
        this.toast = {
          show: true,
          type: "error",
          message: this.t("please_check_required"),
        };
        setTimeout(() => (this.toast.show = false), 2500);

        // ??? button loading end
        this.isSubmitting = false;
        return;
      }
      try {
        const payload = {
          consultant: { ...this.form.consultant },
          user: { ...this.form.user },
        };

        const res = await api.post("/consultantUserRegister", payload);
        const data = res?.data || {};
        const token = data?.token || data?.data?.token || null;

        if (!token) {
          this.errors = data?.errors
            ? this.mapApiErrors(data.errors)
            : { api: data?.message || "Server error" };

          this.toast = {
            show: true,
            type: "error",
            message:
              data?.message ||
              this.t("register_failed") ||
              "Registration failed.",
          };
          setTimeout(() => (this.toast.show = false), 2500);
          return;
        }

        clearAuthCookies();
        setVerifySessionFromApi(data, "register");

        this.toast = {
          show: true,
          type: "success",
          message: data?.message || this.t("register_success") || "Registered successfully.",
        };

        this.$router.replace({ name: "VerifyAccount" });
      } catch (e) {
        const data = e?.response?.data;
        const message =
          data?.message ||
          e?.message ||
          this.t("register_failed") ||
          "Registration failed.";

        this.errors = data?.errors
          ? this.mapApiErrors(data.errors)
          : { api: message };

        this.toast = { show: true, type: "error", message };
        setTimeout(() => (this.toast.show = false), 2500);
      } finally {
        // ✅ always stop loading
        this.isSubmitting = false;
      }
    },
  },

  beforeUnmount() {
    window.removeEventListener("app:lang-changed", this.onLangChanged);
    if (this._langObserver) this._langObserver.disconnect();
  },
};
</script>
